import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import Algolia from "~/types/algolia";
import CrossCountry from "~/types/cross-country";
import gridUtils from "~/utils/grid-utils";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import LinkWrapper from "../common/link-wrapper";
import NextImage from "../common/next-image";
import ProductActions from "../common/product-actions";
import ProductBadge from "../product-badge/product-badge";
import ProductColorSelector from "./product-color-selector";
import ProductListingTags from "./product-listing-tags";
import ProductPrice from "./product-price";
import ProductRatings from "./product-ratings";
import styles from "./product-tile.module.scss";
import ProductTileDialog from "./product-tile-dialog";

type Props = {
  product: CrossCountry.Product;
  idxInList?: number;
  listScope?: string;
  noLazy?: boolean;
  imageLoaded?: () => void;
  algolia?: Algolia.ProductTileInfo;
  isInCarousel?: boolean;
};

export default function ProductTile(props: Props) {
  const t = useTranslations();

  const hasChildren = props.product.children && Array.isArray(props.product.children);
  const [currentDialog, setCurrentDialog] = useState<"colors" | "notifyMe" | null>(null);
  const [source, setSource] = useState<CrossCountry.ChildrenProduct | CrossCountry.Product>(
    !!props.algolia
      ? props.product
      : productUtils.getFirstAvailableProductOtherwiseGetFirstOOS(props.product, hasChildren)
  );
  const [focusOnSelectorBtn, setFocusOnSelectorBtn] = useState<boolean>(false);

  const isGiftCard = productUtils.isGiftCard(props.product);
  const productUrl = source.slug + "-" + source.product_id;
  const TitleTag = props.isInCarousel ? "h3" : "h2";
  const DescTag = props.isInCarousel ? "div" : "h2";
  const isAlgoliaSearch = props.listScope === "search" && !!props.algolia;

  const _trackItem = (
    event: string,
    props: Props,
    variant: CrossCountry.ChildrenProduct,
    payload: Record<string, any> = {}
  ) => {
    TrackingUtils.track(
      TrackingUtils.mapEcommerceEvent(
        event,
        [
          TrackingUtils.mapEcomItem(props.product, variant, {
            index: props.idxInList,
            item_list_name: isAlgoliaSearch ? "algolia_search" : props.listScope,
          }),
        ],
        payload
      )
    );
  };

  useEffect(() => {
    const product = props.algolia?.discountedAndOOSProducts?.[source.product_id];

    if (!!product) {
      setSource((prev) => {
        return {
          ...prev,
          has_stock: product?.hasStock ?? true,
          discounts: product?.discounts ?? [],
        };
      });
    }
  }, [props.algolia?.discountedAndOOSProducts, source.product_id]);

  const productTileTracking = () => {
    _trackItem("select_item", props, source);
    props.algolia?.events?.clickEvent?.(props.algolia.product);
  };

  return (
    <div className={styles.container}>
      <ProductBadge product={source} />
      <Link className={styles.productLink} href={`/p/${productUrl}`} onClick={productTileTracking}>
        <div className={styles.image}>
          <NextImage
            priority={props.noLazy}
            src={source.product_media?.primary_image?.url || imageUtils.transparentPixel}
            alt={props.product.display_value}
            fill
            sizes={gridUtils.imageSizes}
            className={styles.media}
            imageLoaded={props.imageLoaded}
          />
        </div>
        <ProductListingTags product={source} className={styles.containerTags} keepContainer />
        <DescTag className={styles.description} dangerouslySetInnerHTML={{ __html: props.product.short_description }} />
        <TitleTag className={styles.name}>{props.product.display_value}</TitleTag>
      </Link>
      <div className={styles.ratings}>
        {process.env.NEXT_PUBLIC_POWERREVIEWS_DISPLAY_DATA_FROM_EVA !== "false" && (
          <ProductRatings
            average={props.product.review_average_score}
            numberOfRatings={props.product.review_total_number}
          />
        )}
      </div>
      <div className={styles.price}>
        <ProductPrice product={source} />
      </div>
      <div className={styles.colorSelector}>
        {hasChildren && (
          <ProductColorSelector
            product={props.product}
            current={source}
            onSelect={() => setCurrentDialog("colors")}
            currentDialog={currentDialog}
            focusOnSelectorBtn={focusOnSelectorBtn}
          />
        )}
      </div>
      <div className={styles.button}>
        {isGiftCard ? (
          <LinkWrapper href={`/p/${productUrl}`} lookAsButton="primary" className={styles.discoverMoreButton}>
            {t("generic.discoverMore")}
          </LinkWrapper>
        ) : (
          <ProductActions
            hasPrice={productUtils.hasValidDisplayPrice(source)}
            productId={source.product_id}
            maxQuantity={source.ecomm_max_items_per_order}
            outOfStock={!source.has_stock}
            onClickNotifyMe={() => {
              if (productUtils.hasValidDisplayPrice(source)) {
                setCurrentDialog("notifyMe");
              }
            }}
            actionsContext={props.listScope}
            trackingData={TrackingUtils.mapEcomItem(props.product, source, {
              item_list_name: props.listScope,
            })}
            algolia={props.algolia}
          />
        )}
      </div>
      <ProductTileDialog
        {...{
          currentDialog,
          setCurrentDialog,
          setFocusOnSelectorBtn,
          source,
          setSource,
          product: props.product,
          hasChildren,
          _trackItem,
          idxInList: props.idxInList,
          listScope: props.listScope,
          isAlgolia: !!props.algolia,
        }}
      />
    </div>
  );
}
